import type {
  CustomerAuthPayload,
  CustomerLoginInput,
  CustomerLoginResponse,
  CustomerSignupInput,
} from '../types'
import type { CustomerActivationValidationStatus } from './types'
import { requestCustomerAuthJson } from './core'

export const signupCustomer = (input: CustomerSignupInput) =>
  requestCustomerAuthJson<{ message: string }>('/auth/customer/signup', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({
      email: input.email,
      firstName: input.firstName,
      lastName: input.lastName,
      taxCode: input.taxCode,
      phone: input.phone,
      residentialAddress: input.residentialAddress,
      botProtection: input.botProtection,
    }),
  })

export const loginCustomer = (input: CustomerLoginInput) =>
  requestCustomerAuthJson<CustomerLoginResponse>('/auth/customer/login', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({
      identifier: input.identifier,
      password: input.password,
    }),
  })

export const verifyCustomerLoginSecondFactor = (input: { sessionId: string; code: string }) =>
  requestCustomerAuthJson<CustomerAuthPayload>('/auth/customer/login/2fa/verify', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({
      sessionId: input.sessionId,
      code: input.code,
    }),
  })

export const requestCustomerLoginPasskeyOptions = (sessionId: string) =>
  requestCustomerAuthJson<Record<string, unknown>>('/passkeys/auth/options', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ sessionId }),
  })

export const verifyCustomerLoginPasskey = (input: {
  sessionId: string
  response: Record<string, unknown>
}) =>
  requestCustomerAuthJson<CustomerAuthPayload>('/passkeys/auth/verify', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({
      sessionId: input.sessionId,
      response: input.response,
    }),
  })

export const refreshCustomerSession = () =>
  requestCustomerAuthJson<CustomerAuthPayload>('/auth/customer/refresh', {
    method: 'POST',
    credentials: 'include',
  })

export const forgotPassword = (email: string) =>
  requestCustomerAuthJson<{ message: string }>('/auth/customer/forgot-password', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({
      email,
    }),
  })

export const resetPassword = (token: string, password: string) =>
  requestCustomerAuthJson<{ message: string }>('/auth/customer/reset-password', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({
      token,
      password,
    }),
  })

export const validateResetPasswordToken = (token: string) =>
  requestCustomerAuthJson<{ status: CustomerActivationValidationStatus }>(
    `/customer/reset-password/validate?token=${encodeURIComponent(token)}`,
    {
      method: 'GET',
    },
  )

export const activateCustomer = (token: string, password: string) =>
  requestCustomerAuthJson<{ message: string }>('/auth/customer/activate', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({
      token,
      password,
    }),
  })

export const validateActivationToken = (token: string) =>
  requestCustomerAuthJson<{ status: CustomerActivationValidationStatus }>(
    `/customer/activation/validate?token=${encodeURIComponent(token)}`,
    {
      method: 'GET',
    },
  )

export const resendActivationByEmail = (email: string) =>
  requestCustomerAuthJson<{ message: string }>('/auth/resend-activation', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ email }),
  })
